import React from 'react';

import { Button, Card } from '@/components/ui';
import { adminDeleteUser, adminSetUserPassword, adminUpdateUserProfile } from '@/lib/auth/client-auth-service';
import { canManageUsers } from '@/lib/roles';
import { formatBsb, formatAccountNumber } from '@/lib/utils/bsb';
import type { User } from '@/lib/types/core';

type EditableUser = Pick<
  User,
  'id' | 'name' | 'email' | 'telegram_username' | 'beem_id' | 'is_active' | 'roles' | 'eft_account_name' | 'eft_bsb' | 'eft_account_number'
> & {
  auto_add_to_game?: boolean;
};

type Props = {
  currentUser: Pick<User, 'id' | 'roles' | 'is_active'> | null;
  user: EditableUser;
};

const SAVE_MESSAGE = 'User saved. Changes take effect after reload/sign-in.';
const SAVE_MESSAGE_STORAGE_KEY = 'poker-knights:admin-users:save-message';
const TARGET_USER_PARAM = 'user';

function readStoredSaveMessage(userId: string): string | null {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const stored = window.sessionStorage.getItem(SAVE_MESSAGE_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { userId?: string; message?: string };
    return parsed.userId === userId && parsed.message ? parsed.message : null;
  } catch {
    window.sessionStorage.removeItem(SAVE_MESSAGE_STORAGE_KEY);
    return null;
  }
}

function storeSaveMessage(userId: string, message: string) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(SAVE_MESSAGE_STORAGE_KEY, JSON.stringify({ userId, message }));
}

function syncTargetUserParam(userId: string) {
  if (typeof window === 'undefined') return;
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(TARGET_USER_PARAM, userId);
  window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
}

function cloneEditableUser(user: EditableUser): EditableUser {
  return {
    ...user,
    roles: { ...user.roles },
  };
}

export function AdminUserDetailForm({ currentUser, user }: Props) {
  const isSelf = user.id === currentUser?.id;
  const [draft, setDraft] = React.useState<EditableUser>(() => cloneEditableUser(user));
  const [message, setMessage] = React.useState<string | null>(() => readStoredSaveMessage(user.id));
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [passwordDraft, setPasswordDraft] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordSaving, setPasswordSaving] = React.useState(false);
  const [passwordMessage, setPasswordMessage] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<Pick<EditableUser, 'id' | 'name'> | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteMessage, setDeleteMessage] = React.useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = React.useState(false);

  React.useEffect(() => {
    setDraft(cloneEditableUser(user));
    setMessage(readStoredSaveMessage(user.id));
    setError(null);
    syncTargetUserParam(user.id);
  }, [user.id]);

  if (!canManageUsers(currentUser)) return null;

  const updateRole = (key: 'player' | 'entertainment_admin' | 'app_admin', value: boolean) => {
    setDraft((previous) => (previous
      ? { ...previous, roles: { ...previous.roles, [key]: value } }
      : previous));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      await adminUpdateUserProfile(draft.id, {
        fields: {
          name: draft.name,
          email: draft.email,
          telegram_username: draft.telegram_username?.trim() || null,
          beem_id: draft.beem_id?.trim() || null,
          eft_account_name: draft.eft_account_name?.trim() || null,
          eft_bsb: draft.eft_bsb?.trim() || null,
          eft_account_number: draft.eft_account_number?.trim() || null,
          is_active: draft.is_active,
          roles: {
            player: draft.roles?.player === true,
            entertainment_admin: draft.roles?.entertainment_admin === true,
            app_admin: isSelf ? true : draft.roles?.app_admin === true,
          },
        },
      });
      storeSaveMessage(draft.id, SAVE_MESSAGE);
      setMessage(SAVE_MESSAGE);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordMessage(null);
    if (!passwordDraft || passwordDraft.length < 6) {
      setPasswordMessage('Password must be at least 6 characters.');
      return;
    }
    if (passwordDraft !== passwordConfirm) {
      setPasswordMessage('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await adminSetUserPassword(draft.id, passwordDraft);
      setPasswordDraft('');
      setPasswordConfirm('');
      setPasswordMessage('Password set. User must change it on next sign-in.');
    } catch (passwordError) {
      setPasswordMessage(passwordError instanceof Error ? passwordError.message : 'Failed to set password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteMessage(null);
    setDeleteSuccess(false);
    try {
      await adminDeleteUser(deleteTarget.id);
      setDeleteMessage(`${deleteTarget.name} has been permanently deleted.`);
      setDeleteSuccess(true);
      setDeleteTarget(null);
    } catch (error) {
      setDeleteMessage(error instanceof Error ? error.message : 'Failed to delete user.');
      setDeleteSuccess(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className="text-sm font-medium text-[#006855] bg-[#dcfce7] rounded-lg p-3">{message}</div>
      )}
      {error && (
        <div className="text-sm font-medium text-red-700 bg-red-100 rounded-lg p-3">{error}</div>
      )}

      <form onSubmit={save} className="space-y-6">
        {/* Profile Details */}
        <Card className="border border-gray-200">
          <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Profile Details</h2>
          <div className="space-y-3">
            <div>
              <label htmlFor="admin-users-name" className="block text-xs font-medium text-[#6b7280] mb-1">
                Display Name
              </label>
              <input
                id="admin-users-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div>
              <label htmlFor="admin-users-email" className="block text-xs font-medium text-[#6b7280] mb-1">
                Email
              </label>
              <input
                id="admin-users-email"
                type="email"
                value={draft.email ?? ''}
                onChange={(event) => setDraft({ ...draft, email: event.target.value || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div>
              <label htmlFor="admin-users-telegram" className="block text-xs font-medium text-[#6b7280] mb-1">
                Telegram Username
              </label>
              <input
                id="admin-users-telegram"
                value={draft.telegram_username ?? ''}
                onChange={(event) => setDraft({ ...draft, telegram_username: event.target.value || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div>
              <label htmlFor="admin-users-beem" className="block text-xs font-medium text-[#6b7280] mb-1">
                Beem ID
              </label>
              <input
                id="admin-users-beem"
                value={draft.beem_id ?? ''}
                onChange={(event) => setDraft({ ...draft, beem_id: event.target.value || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>
          </div>
        </Card>

        {/* Bank Details */}
        <Card className="border border-gray-200">
          <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Bank Details</h2>
          <div className="space-y-3">
            <div>
              <label htmlFor="admin-users-eft-account-name" className="block text-xs font-medium text-[#6b7280] mb-1">
                Account Name
              </label>
              <input
                id="admin-users-eft-account-name"
                value={draft.eft_account_name ?? ''}
                onChange={(event) => setDraft({ ...draft, eft_account_name: event.target.value || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div>
              <label htmlFor="admin-users-eft-bsb" className="block text-xs font-medium text-[#6b7280] mb-1">
                BSB
              </label>
              <input
                id="admin-users-eft-bsb"
                placeholder="XXX-XXX"
                value={draft.eft_bsb ?? ''}
                onChange={(event) => setDraft({ ...draft, eft_bsb: formatBsb(event.target.value) || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div>
              <label htmlFor="admin-users-eft-account-number" className="block text-xs font-medium text-[#6b7280] mb-1">
                Account Number
              </label>
              <input
                id="admin-users-eft-account-number"
                value={draft.eft_account_number ?? ''}
                onChange={(event) => setDraft({ ...draft, eft_account_number: formatAccountNumber(event.target.value) || null })}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>
          </div>
        </Card>

        {/* Admin Controls */}
        <Card className="border-red-200">
          <h2 className="text-[10px] font-black text-red-600 uppercase tracking-[2px] mb-4">Admin Controls</h2>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.is_active === true}
                onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-semibold text-[#2c2f30]">Active Account</div>
                <div className="text-xs text-[#6b7280]">Enable or disable this user's account</div>
              </div>
            </label>

            <div className="space-y-3">
              <div className="text-xs font-medium text-[#6b7280]">Roles</div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  aria-label="Player role"
                  type="checkbox"
                  checked={draft.roles?.player === true}
                  onChange={(event) => updateRole('player', event.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-[#2c2f30]">Player</div>
                  <div className="text-xs text-[#6b7280]">Can join games, buy chips, participate in entertainment</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  aria-label="Entertainment Admin role"
                  type="checkbox"
                  checked={draft.roles?.entertainment_admin === true}
                  onChange={(event) => updateRole('entertainment_admin', event.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-[#2c2f30]">Entertainment Admin</div>
                  <div className="text-xs text-[#6b7280]">Can approve expenses, manage entertainment categories</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  aria-label="Admin role"
                  type="checkbox"
                  checked={draft.roles?.app_admin === true}
                  disabled={isSelf}
                  onChange={(event) => updateRole('app_admin', event.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-[#2c2f30]">Admin</div>
                  <div className="text-xs text-[#6b7280]">Can manage users, edit profiles, access admin pages</div>
                  {isSelf && (
                    <div className="text-xs text-red-500 mt-0.5">You cannot remove your own admin role</div>
                  )}
                </div>
              </label>
            </div>
          </div>
        </Card>

        <Button type="submit" fullWidth disabled={saving}>
          {saving ? 'Saving...' : 'Save User'}
        </Button>
      </form>

      {/* Set Password */}
      <Card className="border border-gray-200">
        <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Set Password</h2>
        <form onSubmit={savePassword} className="space-y-3">
          <div className="relative">
            <label htmlFor="admin-set-password-new" className="block text-xs font-medium text-[#6b7280] mb-1">
              New Password
            </label>
            <input
              id="admin-set-password-new"
              type={showPassword ? 'text' : 'password'}
              value={passwordDraft}
              autoComplete="new-password"
              onChange={(event) => setPasswordDraft(event.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 pr-10 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-[calc(50%+6px)] -translate-y-1/2 text-[#6b7280] hover:text-[#2c2f30]"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <div>
            <label htmlFor="admin-set-password-confirm" className="block text-xs font-medium text-[#6b7280] mb-1">
              Confirm Password
            </label>
            <input
              id="admin-set-password-confirm"
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              autoComplete="new-password"
              onChange={(event) => setPasswordConfirm(event.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>
          <Button type="submit" fullWidth disabled={passwordSaving}>
            {passwordSaving ? 'Setting Password...' : 'Set Password'}
          </Button>
        </form>
        {passwordMessage && (
          <div className={`mt-3 text-sm font-semibold ${passwordMessage.startsWith('Password set') ? 'text-[#006855]' : 'text-red-600'}`}>
            {passwordMessage}
          </div>
        )}
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <h2 className="text-[10px] font-black text-red-600 uppercase tracking-[2px] mb-4">Danger Zone</h2>
        {!deleteTarget ? (
          <Button
            type="button"
            variant="danger"
            fullWidth
            disabled={isSelf || deleting}
            title={isSelf ? 'You cannot delete your own account.' : undefined}
            onClick={() => setDeleteTarget({ id: draft.id, name: draft.name })}
          >
            Delete User
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-red-600">
              Permanently delete <span className="font-extrabold">{deleteTarget.name}</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                fullWidth
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                fullWidth
                disabled={deleting}
                onClick={deleteUser}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </Button>
            </div>
          </div>
        )}
        {deleteMessage && (
          <div className={`mt-3 text-sm font-semibold ${deleteSuccess ? 'text-[#006855]' : 'text-red-600'}`}>
            {deleteMessage}
          </div>
        )}
      </Card>
    </div>
  );
}
